<script setup lang="ts">
    import { inject, onMounted } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { BackOfficeDashboardAccess } from './BackOfficeDashboard.access';
    import { formatNumber } from '@cwd/Shared/utils/formatNumber';
    import Loading from '@shared/components/Loading.vue';
    import IndustryChart from './IndustryChart.vue';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const dashboardAccess: BackOfficeDashboardAccess = injectorAccess.injector.get(BackOfficeDashboardAccess);

    onMounted(async () => {
        await Promise.all([
            dashboardAccess.getCurrentWorkStatistics(),
            dashboardAccess.getBounceSummary(),
            dashboardAccess.getHealthData(),
            dashboardAccess.getTaskErrors(),
            dashboardAccess.getIndustryCounts(),
        ]);
    });
</script>

<template>
    <div>
        <Loading :show="dashboardAccess.state.loading" :fixed="true" />
        <h1>Dashboard</h1>

        <IndustryChart :dashboard-access="dashboardAccess" />

        <h2 class="h4">Task Errors</h2>
        <div class="stat-highlights panel panel-default">
            <div class="row no-gutter d-flex flex-wrap">
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">Failed Tasks</p>
                        <p class="value">
                            <router-link :to="{ name: 'HealthManager' }">{{ formatNumber(dashboardAccess.state.taskErrorCount ?? null) || '-' }}</router-link>
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <h2 class="h4">This week</h2>
        <div class="stat-highlights panel panel-default">
            <div class="row no-gutter d-flex flex-wrap">
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">Mailings sent</p>
                        <p class="value">
                            <span>{{ formatNumber(dashboardAccess.state.currentWorkStatistics?.Issues.Count ?? null) || '-' }}</span>
                        </p>
                    </div>
                </div>
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">Amount of recipients (mailings)</p>
                        <p class="value">
                            <span>{{ formatNumber(dashboardAccess.state.currentWorkStatistics?.Issues.RecipientCount ?? null) || '-' }}</span>
                        </p>
                    </div>
                </div>
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">SMS sent</p>
                        <p class="value">
                            <span>{{ formatNumber(dashboardAccess.state.currentWorkStatistics?.SmsMessages.Count ?? null) || '-' }}</span>
                        </p>
                    </div>
                </div>
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">SMS received</p>
                        <p class="value">
                            <span>{{ formatNumber(dashboardAccess.state.currentWorkStatistics?.SmsMessages.SentCount ?? null) || '-' }}</span>
                            <small>
                                /
                                <span>{{ formatNumber(dashboardAccess.state.currentWorkStatistics?.SmsMessages.RecipientCount ?? null) || '-' }}</span>
                            </small>
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div class="stat-highlights panel panel-default">
            <div class="row no-gutter d-flex flex-wrap">
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">Import tasks</p>
                        <p class="value">
                            <span>{{ formatNumber(dashboardAccess.state.currentWorkStatistics?.Imports.Count ?? null) || '-' }}</span>
                        </p>
                    </div>
                </div>
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">Imported contacts</p>
                        <p class="value">
                            <span>{{ formatNumber(dashboardAccess.state.currentWorkStatistics?.Imports.ContactCount ?? null) || '-' }}</span>
                        </p>
                    </div>
                </div>
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">Surveys published</p>
                        <p class="value">
                            <span>{{ formatNumber(dashboardAccess.state.currentWorkStatistics?.Surveys.Count ?? null) || '-' }}</span>
                        </p>
                    </div>
                </div>
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">Survey responses</p>
                        <p class="value">
                            <span>{{ formatNumber(dashboardAccess.state.currentWorkStatistics?.Surveys.ResponseCount ?? null) || '-' }}</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div class="stat-highlights panel panel-default">
            <div class="row no-gutter d-flex flex-wrap">
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">Accounts created</p>
                        <p class="value">
                            <span>{{ formatNumber(dashboardAccess.state.currentWorkStatistics?.Accounts.Count ?? null) || '-' }}</span>
                        </p>
                    </div>
                </div>
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">Accounts enabled</p>
                        <p class="value">
                            <span>{{ formatNumber(dashboardAccess.state.currentWorkStatistics?.Accounts.Enabled ?? null) || '-' }}</span>
                        </p>
                    </div>
                </div>
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">User invitations sent</p>
                        <p class="value">
                            <span>{{ formatNumber(dashboardAccess.state.currentWorkStatistics?.AccountUserInvites.Count ?? null) || '-' }}</span>
                        </p>
                    </div>
                </div>
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">User invitations accepted</p>
                        <p class="value">
                            <span>{{ formatNumber(dashboardAccess.state.currentWorkStatistics?.AccountUserInvites.Accepted ?? null) || '-' }}</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <h2 class="h4">Bounces</h2>

        <div class="form-group m-sm-b">
            <button
                class="btn btn-xs btn-default"
                @click="dashboardAccess.getBounceSummary"
                v-if="!dashboardAccess.state.bounceSummary && !dashboardAccess.state.loading">
                Load stats
            </button>
        </div>

        <div class="stat-highlights panel panel-default">
            <div class="row no-gutter d-flex flex-wrap">
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">Bounces last 24h</p>
                        <p class="value">
                            <span>{{ formatNumber(dashboardAccess.state.bounceSummary?.BouncesLast24h ?? null) || '-' }}</span>
                        </p>
                    </div>
                </div>
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">Bounces last 7 days</p>
                        <p class="value">
                            <span>{{ formatNumber(dashboardAccess.state.bounceSummary?.BouncesLast7d ?? null) || '-' }}</span>
                        </p>
                    </div>
                </div>
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">Bounce per recipient last 24h</p>
                        <p class="value">
                            <span>
                                {{
                                    dashboardAccess.state.bounceSummary
                                        ? formatNumber(
                                              dashboardAccess.state.bounceSummary.RecipientsLast24h === 0
                                                  ? 0
                                                  : (dashboardAccess.state.bounceSummary.BouncesLast24h /
                                                        dashboardAccess.state.bounceSummary.RecipientsLast24h) *
                                                        100,
                                              2
                                          ) + '%'
                                        : '-'
                                }}
                            </span>
                        </p>
                    </div>
                </div>
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">Bounce per recipient last 7 days</p>
                        <p class="value">
                            <span>
                                {{
                                    dashboardAccess.state.bounceSummary
                                        ? formatNumber(
                                              dashboardAccess.state.bounceSummary.RecipientsLast7d === 0
                                                  ? 0
                                                  : (dashboardAccess.state.bounceSummary.BouncesLast7d / dashboardAccess.state.bounceSummary.RecipientsLast7d) *
                                                        100,
                                              2
                                          ) + '%'
                                        : '-'
                                }}
                            </span>
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <h2 class="h4">Faults</h2>
        <div class="stat-highlights panel panel-default">
            <div class="row no-gutter d-flex flex-wrap">
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">Faulted messages in error queue</p>
                        <p class="value">
                            <span>{{ formatNumber(dashboardAccess.state.healthData?.FaultedMessageCount ?? null) || '-' }}</span>
                        </p>
                    </div>
                </div>
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">Failed tasks today</p>
                        <p class="value">
                            <span>{{ formatNumber(dashboardAccess.state.healthData?.FailedToday ?? null) || '-' }}</span>
                        </p>
                    </div>
                </div>
                <div class="flex-col col-lg-3 col-md-3 col-sm-6 col-xs-6">
                    <div class="stat-highlights-body ug-bars">
                        <p class="bar-label">Failed tasks last 7 days</p>
                        <p class="value">
                            <span>{{ formatNumber(dashboardAccess.state.healthData?.FailedLast7Days ?? null) || '-' }}</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .value {
        font-size: 32px !important;
        font-weight: 600 !important;
    }
</style>
